/**
 * The secondary nav bar for the meeting screens.
 */

import { useState } from 'preact/hooks';
import { BtnPrimary } from '@components/buttons';
import { meetingUrl, seriesUrl } from './urls';
import {
  PageNavBar,
  PageNavBody,
  PageNavLink,
  PageNavSegment,
} from '@course/components/page-nav-bar';
import { Meeting, MeetingSeryRow } from 'server/types';
import { fmtFullTime } from 'shared/dateutil';
import { useRouteParams } from '@components/router';
import { Pill } from '@components/pill';
import { MeetingTypeIcons } from '../student-meetings/nav-bar';
import { useCurrentTenant } from '@components/router/session-context';
import { useMeetingStatus } from 'client/lib/hooks/use-meeting-status';

interface Props {
  draft: Meeting[];
  upcoming: Meeting[];
  past: Meeting[];
  series?: MeetingSeryRow[];
}

function Item({ meeting, isPast }: { meeting: Meeting; isPast: boolean }) {
  const { courseId, meetingId } = useRouteParams();
  const status = useMeetingStatus(meeting);

  return (
    <PageNavLink
      isSelected={meeting.id === meetingId}
      href={meetingUrl({ courseId, meetingId: meeting.id })}
    >
      <span class="flex grow py-2 gap-3">
        <span class="pt-1">{MeetingTypeIcons[meeting.type]}</span>
        <span class="flex grow flex-col">
          <strong class="flex justify-between gap-2">
            <span class={meeting.disruption ? 'line-through opacity-60' : ''}>{meeting.title}</span>
            {!isPast && status === 'inProgress' && !meeting.disruption && (
              <Pill color="green">ON AIR</Pill>
            )}
            {meeting.disruption === 'cancelled' && <Pill color="red">CANCELLED</Pill>}
            {meeting.disruption === 'skipped' && <Pill color="yellow">SKIPPED</Pill>}
          </strong>
          <span class="opacity-75 font-normal text-xs">
            {isPast ? 'Aired on' : ''} {fmtFullTime(meeting.scheduledAt)}
          </span>
        </span>
      </span>
    </PageNavLink>
  );
}

type Bucket = {
  oneOffs: Meeting[];
  bySeries: Map<UUID, Meeting[]>;
};

function partition(meetings: Meeting[]): Bucket {
  const oneOffs: Meeting[] = [];
  const bySeries = new Map<UUID, Meeting[]>();
  for (const m of meetings) {
    if (m.seriesId) {
      const list = bySeries.get(m.seriesId) ?? [];
      list.push(m);
      bySeries.set(m.seriesId, list);
    } else {
      oneOffs.push(m);
    }
  }
  return { oneOffs, bySeries };
}

function SeriesGroup({
  series,
  sessions,
  isPast,
}: {
  series: MeetingSeryRow;
  sessions: Meeting[];
  isPast: boolean;
}) {
  const { courseId } = useRouteParams();
  const [open, setOpen] = useState(!isPast);
  const next = sessions.find((s) => !!s.scheduledAt);
  return (
    <div class="border-l-2 border-gray-200 dark:border-gray-700 pl-2 my-1">
      <button
        type="button"
        class="flex w-full items-center justify-between py-1 px-1 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 rounded"
        onClick={() => setOpen((v) => !v)}
      >
        <span class="flex flex-col">
          <span class="font-medium flex items-center gap-1">
            <span class="text-gray-500">{open ? '▼' : '▶'}</span>
            Recurring: {series.title}
          </span>
          <span class="text-xs opacity-75">
            {sessions.length} session{sessions.length === 1 ? '' : 's'}
            {next && !isPast ? ` • next ${fmtFullTime(next.scheduledAt)}` : ''}
          </span>
        </span>
      </button>
      {open && (
        <div class="flex flex-col gap-1 mt-1">
          {sessions.map((m) => (
            <Item key={m.id} meeting={m} isPast={isPast} />
          ))}
          <a
            href={seriesUrl({ courseId, seriesId: series.id })}
            class="text-xs text-blue-600 hover:underline pl-3 py-1"
          >
            Open series →
          </a>
        </div>
      )}
    </div>
  );
}

function renderBucket({
  meetings,
  series,
  isPast,
}: {
  meetings: Meeting[];
  series: MeetingSeryRow[];
  isPast: boolean;
}) {
  const { oneOffs, bySeries } = partition(meetings);
  const knownSeries = new Map(series.map((s) => [s.id, s]));
  const orphanedSessions: Meeting[] = [];
  const groupedEntries: Array<{ sid: UUID; series: MeetingSeryRow }> = [];
  for (const sid of bySeries.keys()) {
    const ser = knownSeries.get(sid);
    if (ser) {
      groupedEntries.push({ sid, series: ser });
    } else {
      orphanedSessions.push(...(bySeries.get(sid) ?? []));
    }
  }
  return (
    <>
      {groupedEntries.map(({ sid, series: ser }) => (
        <SeriesGroup key={sid} series={ser} sessions={bySeries.get(sid) ?? []} isPast={isPast} />
      ))}
      {oneOffs.concat(orphanedSessions).map((meeting) => (
        <Item key={meeting.id} meeting={meeting} isPast={isPast} />
      ))}
    </>
  );
}

export function NavBar({ draft, upcoming, past, series }: Props) {
  const { courseId } = useRouteParams();
  const { terminology } = useCurrentTenant();
  const seriesList = series ?? [];

  return (
    <PageNavBar title={terminology.Meetings}>
      <BtnPrimary class="w-full py-3" href={meetingUrl({ courseId, page: 'new' })}>
        Create {terminology.Meeting}
      </BtnPrimary>
      <PageNavBody>
        {draft.length > 0 && (
          <PageNavSegment title="Draft" numItems={draft.length}>
            {renderBucket({ meetings: draft, series: seriesList, isPast: false })}
          </PageNavSegment>
        )}
        <PageNavSegment title="Upcoming" numItems={upcoming.length}>
          {renderBucket({ meetings: upcoming, series: seriesList, isPast: false })}
        </PageNavSegment>
        <PageNavSegment title="Past" numItems={past.length}>
          {renderBucket({ meetings: past, series: seriesList, isPast: true })}
        </PageNavSegment>
      </PageNavBody>
    </PageNavBar>
  );
}
